import { useCandidateEventIndex } from '@jobilla/nexus-sdk'
import { useMemo } from 'react'
import { type InterviewMeeting, LIVE_COUNTDOWN_WITHIN_MS, mapEventBooking, useApproachingNow } from './meeting-card'

/** Beyond this far from the start, the Jobilla Meet tab offers an instant call instead. */
const INSTANT_THRESHOLD_MS = 30 * 60 * 1000

export interface ScheduledJobillaMeet {
	bookingId: string
	meeting: InterviewMeeting
	/** Joinable now (joinable_at ≤ now < ends_at) — the room is live. Past the meeting's end time
	 * the Digital Samba session is over, so we stop offering to join even within the token grace. */
	isJoinable: boolean
	/** More than 30 minutes before the start — the tab offers an instant call rather than this one. */
	isFarOff: boolean
}

/**
 * The candidate's soonest confirmed Jobilla Meet interview whose join window hasn't fully passed,
 * with live `isJoinable` / `isFarOff` flags that flip as time passes (ticking toward the next
 * boundary so the tab updates without a reload). Returns null when there's no such interview.
 * Shares the candidate-events query with the interviews timeline, so it costs no extra request.
 */
export function useScheduledJobillaMeet(candidateId: string): ScheduledJobillaMeet | null {
	const { data: eventIndex } = useCandidateEventIndex(candidateId)

	const meetings = useMemo(() => {
		return (eventIndex?.events ?? [])
			.map((event) => event.booking)
			.filter((booking): booking is NonNullable<typeof booking> =>
				booking != null
				&& booking.status === 'confirmed'
				&& booking.meeting.type === 'jobilla_meet'
				&& booking.meeting.recruiterLink != null
			)
			.map((booking) => ({ bookingId: booking.id, meeting: mapEventBooking(booking).meeting }))
			.sort((first, second) => first.meeting.startsAt.getTime() - second.meeting.startsAt.getTime())
	}, [eventIndex])

	const referenceNow = Date.now()
	// Only meetings whose end time is still ahead — once the meeting's time has passed the room
	// can't be joined, so it drops out here rather than lingering as a dead "Join now".
	const active = meetings.find((entry) => entry.meeting.endsAt.getTime() > referenceNow) ?? null

	const boundary = active
		? (referenceNow < active.meeting.joinableAt.getTime() ? active.meeting.joinableAt : active.meeting.endsAt)
		: null
	const now = useApproachingNow(boundary, LIVE_COUNTDOWN_WITHIN_MS)

	if (!active) {
		return null
	}

	const nowMs = now.getTime()

	return {
		bookingId: active.bookingId,
		meeting: active.meeting,
		isJoinable: nowMs >= active.meeting.joinableAt.getTime() && nowMs < active.meeting.endsAt.getTime(),
		isFarOff: active.meeting.startsAt.getTime() - nowMs > INSTANT_THRESHOLD_MS,
	}
}
